import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Send } from "lucide-react";
import { Seo } from "../components/Seo";
import { MaskedLine, Reveal } from "../components/Reveal";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { SERVICES } from "../data/services";

const TIMES = ["Morning (9am–12pm ET)", "Midday (12–3pm ET)", "Afternoon (3–6pm ET)"];

const inputProps = { required: true };

export default function Contact() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "book" ? "book" : "message");
  const [busy, setBusy] = useState(false);
  const [contact, setContact] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [attachment, setAttachment] = useState(null);
  const [booking, setBooking] = useState({
    name: "", company: "", email: "", phone: "",
    preferred_date: "", preferred_time: TIMES[0], topic: SERVICES[0].name, message: "",
  });

  const submit = async (e, kind) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (kind === "contact") {
        let attachment_id = "";
        if (attachment) {
          const data = new FormData();
          data.append("file", attachment);
          const up = await apiClient.post("/uploads", data);
          attachment_id = up.data.id;
        }
        await apiClient.post("/contact", { ...contact, attachment_id });
        toast.success("Message received. We'll be in touch shortly.");
        setContact({ name: "", company: "", email: "", phone: "", message: "" });
        setAttachment(null);
      } else {
        await apiClient.post("/bookings", booking);
        toast.success("Consultation requested. We'll confirm your time by email shortly.");
        setBooking({ name: "", company: "", email: "", phone: "", preferred_date: "", preferred_time: TIMES[0], topic: SERVICES[0].name, message: "" });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setC = (k) => (e) => setContact({ ...contact, [k]: e.target.value });
  const setB = (k) => (e) => setBooking({ ...booking, [k]: e.target.value });

  return (
    <>
      <Seo
        title="Contact Sempresera | Book a Free IT Consultation"
        description="Talk to Sempresera about managed IT for your small business. Send a message or book a free consultation — serving companies of up to 25 employees across the United States."
        path="/contact"
      />

      <section className="mx-auto max-w-7xl px-5 pb-24 pt-36 md:px-8 md:pt-44" data-testid="contact-page">
        <MaskedLine delay={0.1}>
          <span className="eyebrow">Contact</span>
        </MaskedLine>
        <h1 className="mt-6 max-w-3xl font-display text-4xl font-black leading-[1.02] tracking-tighter text-white md:text-6xl">
          <MaskedLine delay={0.25}>Let's talk about</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-brand">your IT.</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.55}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-[#C9D2DE]">
            No sales scripts, no pressure. Tell us what's slowing your business down and we'll tell you plainly how
            we'd fix it.
          </p>
        </MaskedLine>

        <div className="mt-14 grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="glass p-8">
                <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#A8B4C2]">How it works</p>
                <ol className="mt-6 space-y-6">
                  {[
                    "Send a message or ask us about any specific needs others have been unable to help you with.",
                    "The CEO — not a salesperson — reviews your note.",
                    "You will receive a quote from us in plain English so you know what you're paying for.",
                  ].map((step, i) => (
                    <li key={step} className="flex gap-4">
                      <span className="font-display text-2xl font-black text-brand">0{i + 1}</span>
                      <p className="text-sm leading-relaxed text-[#C9D2DE]">{step}</p>
                    </li>
                  ))}
                </ol>
                <div className="mt-8 border-t border-white/10 pt-6" data-testid="contact-address">
                  <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#A8B4C2]">Mail</p>
                  <p className="mt-2 text-sm leading-relaxed text-[#C9D2DE]">
                    1643 Warwick Ave, Suite 183<br />Warwick, RI 02889
                  </p>
                </div>
                <div className="mt-6" data-testid="contact-ceo">
                  <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-[#A8B4C2]">Talk to the CEO</p>
                  <p className="mt-2 text-sm font-medium text-white">Joey Leite</p>
                  <p className="mt-1 text-sm leading-relaxed text-[#C9D2DE]">
                    <a href="tel:+14013063231" className="text-brand transition-colors duration-200 hover:text-white" data-testid="contact-ceo-phone">
                      401-306-3231
                    </a>
                    <br />
                    <a href="mailto:jleite@sempresera.com" className="text-brand transition-colors duration-200 hover:text-white" data-testid="contact-ceo-email">
                      jleite@sempresera.com
                    </a>
                  </p>
                </div>
                <p className="mt-6 text-xs leading-relaxed text-[#A8B4C2]">
                  Existing client? Call your assigned engineer's cell directly — every Sempresera customer gets a
                  direct line once they transition over.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <div className="glass p-8 md:p-10">
                <div className="flex gap-2" role="tablist" aria-label="Contact options">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mode === "message"}
                    onClick={() => setMode("message")}
                    data-testid="tab-message"
                    className={`flex items-center justify-center gap-2 rounded-full px-5 py-3 text-center font-display text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
                      mode === "message" ? "bg-brand text-black" : "border border-white/15 text-[#C9D2DE] hover:text-white"
                    }`}
                  >
                    <Send size={14} /> Send a message
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mode === "book"}
                    onClick={() => setMode("book")}
                    data-testid="tab-book"
                    className={`flex items-center justify-center gap-2 rounded-full px-5 py-3 text-center font-display text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 ${
                      mode === "book" ? "bg-brand text-black" : "border border-white/15 text-[#C9D2DE] hover:text-white"
                    }`}
                  >
                    <CalendarClock size={14} /> Book a free consultation
                  </button>
                </div>

                {mode === "message" ? (
                  <form className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2" onSubmit={(e) => submit(e, "contact")} data-testid="contact-form">
                    <div>
                      <label className="field-label" htmlFor="c-name">Name</label>
                      <input id="c-name" className="field" value={contact.name} onChange={setC("name")} data-testid="contact-name-input" {...inputProps} />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="c-company">Company</label>
                      <input id="c-company" className="field" value={contact.company} onChange={setC("company")} data-testid="contact-company-input" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="c-email">Email</label>
                      <input id="c-email" type="email" className="field" value={contact.email} onChange={setC("email")} data-testid="contact-email-input" {...inputProps} />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="c-phone">Phone</label>
                      <input id="c-phone" type="tel" className="field" value={contact.phone} onChange={setC("phone")} data-testid="contact-phone-input" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="field-label" htmlFor="c-message">How can we help?</label>
                      <textarea id="c-message" rows={5} className="field" value={contact.message} onChange={setC("message")} data-testid="contact-message-input" {...inputProps} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="field-label" htmlFor="c-attachment">Attachment (optional)</label>
                      <input
                        id="c-attachment"
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,text/plain,text/csv"
                        onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                        className="field file:mr-4 file:rounded-full file:border-0 file:bg-brand file:px-4 file:py-2 file:text-center file:font-display file:text-xs file:font-bold file:uppercase file:tracking-widest file:text-black"
                        data-testid="contact-attachment-input"
                      />
                      <p className="mt-2 text-xs text-[#A8B4C2]">A screenshot or document that helps us understand the issue — JPG, PNG, GIF, WEBP, PDF, TXT or CSV, 5 MB max.</p>
                    </div>
                    <div className="md:col-span-2">
                      <button type="submit" disabled={busy} className="btn-primary disabled:opacity-50" data-testid="contact-submit-button">
                        {busy ? "Sending…" : "Send message"}
                      </button>
                    </div>
                  </form>
                ) : (
                  <form className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2" onSubmit={(e) => submit(e, "booking")} data-testid="booking-form">
                    <div>
                      <label className="field-label" htmlFor="b-name">Name</label>
                      <input id="b-name" className="field" value={booking.name} onChange={setB("name")} data-testid="booking-name-input" {...inputProps} />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="b-company">Company</label>
                      <input id="b-company" className="field" value={booking.company} onChange={setB("company")} data-testid="booking-company-input" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="b-email">Email</label>
                      <input id="b-email" type="email" className="field" value={booking.email} onChange={setB("email")} data-testid="booking-email-input" {...inputProps} />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="b-phone">Phone</label>
                      <input id="b-phone" type="tel" className="field" value={booking.phone} onChange={setB("phone")} data-testid="booking-phone-input" />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="b-date">Preferred date</label>
                      <input id="b-date" type="date" className="field [color-scheme:dark]" value={booking.preferred_date} onChange={setB("preferred_date")} data-testid="booking-date-input" {...inputProps} />
                    </div>
                    <div>
                      <label className="field-label" htmlFor="b-time">Preferred time</label>
                      <select id="b-time" className="field" value={booking.preferred_time} onChange={setB("preferred_time")} data-testid="booking-time-select">
                        {TIMES.map((t) => <option key={t} value={t} className="bg-panel">{t}</option>)}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="field-label" htmlFor="b-topic">What should we focus on?</label>
                      <select id="b-topic" className="field" value={booking.topic} onChange={setB("topic")} data-testid="booking-topic-select">
                        {[...SERVICES.map((s) => s.name), "General IT review"].map((t) => (
                          <option key={t} value={t} className="bg-panel">{t}</option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="field-label" htmlFor="b-message">Anything we should know? (optional)</label>
                      <textarea id="b-message" rows={4} className="field" value={booking.message} onChange={setB("message")} data-testid="booking-message-input" />
                    </div>
                    <div className="md:col-span-2">
                      <button type="submit" disabled={busy} className="btn-primary disabled:opacity-50" data-testid="booking-submit-button">
                        {busy ? "Requesting…" : "Request consultation"}
                      </button>
                      <p className="mt-4 text-xs text-[#A8B4C2]">We confirm every requested time personally by email.</p>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-24 border-t border-white/5 pt-20" id="switching" data-testid="switching-section">
          <Reveal>
            <p className="eyebrow">Evaluating MSPs?</p>
            <h2 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.05] tracking-tight md:text-5xl">
              <span className="text-metallic">How switching works.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#C9D2DE]">
              Too many businesses stay with an IT provider that isn't taking care of them because change feels
              risky. Here's exactly what moving to Sempresera looks like — you stay in control at every step.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: "Tell us what's not working",
                body: "Book a free consultation and talk to a senior engineer — not a salesperson. We review your current setup, your contract situation, and what you'd want done differently.",
              },
              {
                title: "We document everything",
                body: "Before anything changes, we capture your full environment: admin credentials, vendors, licenses, configurations and backups. Nothing stays trapped in your old provider's head — or your old provider's accounts.",
              },
              {
                title: "We handle the handover",
                body: "We deal with your incumbent provider directly, schedule the cutover around your business hours, and migrate monitoring, security and backups with no downtime for your team.",
              },
              {
                title: "You're covered from day one",
                body: "Your team gets onboarded, your 15-minute critical response target starts immediately, and support is unlimited from the first day — no settling-in period, no meter running.",
              },
            ].map((step, i) => (
              <Reveal key={step.title} delay={i * 0.08}>
                <div className="glass h-full p-8" data-testid={`switching-step-${i}`}>
                  <p className="font-display text-3xl font-black text-metallic">0{i + 1}</p>
                  <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-white">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#C9D2DE]">{step.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl text-sm leading-relaxed text-[#A8B4C2]">
              Most switches complete in two to four weeks depending on your environment and contract terms — and
              you'll know the full plan before you sign anything.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
